import type { Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';
import { MetricLabels } from './constants';
import type {
    Profile,
    Performance,
    TraineeReportModel,
    PsychologicalResult,
    PhysiologicalResult,
    CognitiveFitness,
    ReportHeaderData,
} from './model';
import parseCognitiveResults from './parsers/cognitive';
import parsePhysiologicalResults from './parsers/physiological';
import parsePsychologicalResults from './parsers/psychological';
import { Gender } from '@/gql/enums';
import type { TrainersNotesStatus } from '@/gql/enums';
import type {
    AssessmentResultItemDataFragment,
    TenantUserReportDataFragment,
    TrainersNotesAssessmentResultDataFragment,
    PerformanceResultListItemDataFragment,
} from '@/gql/graphql';
import { formatSecondsAsHoursMin, formatSecondsAsMinSec } from '@/utils/common';

export interface FilterState {
    programId?: string;
    assessmentRunId?: string;
}

export interface ListRow {
    id: string;
    userId: string;
    traineeName?: string;
    programmeName: string;
    groupName?: string;
    subGroupName?: string;
    assessmentRunName: string;
    assessmentRunId: string;
    programId?: string;
    startDate?: Date;
    endDate?: Date;
    completionDate?: Date;
    trainersNotesStatus?: TrainersNotesStatus;
    canEdit?: boolean;
}

export enum ActionType {
    View = 'view',
    Download = 'download',
    Edit = 'edit',
}

export const calculateBMI = (weightKg: number, heightMeters: number): number => {
    if (heightMeters <= 0) {
        return 0;
    }
    const bmi = weightKg / (heightMeters * heightMeters);
    return bmi;
};

export const getShortMetricLabel = (t: TFunction, label: string): string => {
    switch (label) {
        case MetricLabels.RunTime:
            return t('traineeReports:performance.ippt.runTimeShort');
        default:
            return label;
    }
};

const parseGender = (t: TFunction, gender: Gender): string => {
    switch (gender) {
        case Gender.Male:
            return t('traineeReports:profile.gender.Male');
        case Gender.Female:
            return t('traineeReports:profile.gender.Female');
        case Gender.NotToSpecify:
        default:
            return t('traineeReports:na');
    }
};

export const mapReportHeaderData = (
    t: TFunction,
    formatDate: (date: string | Date | Dayjs) => string,
    programName: string,
    startDate: Date,
    endDate: Date,
    results: AssessmentResultItemDataFragment[],
    incompleteItems: AssessmentResultItemDataFragment[]
): ReportHeaderData => {
    const now = new Date();
    if (incompleteItems.length > 0) {
        if (now > endDate) {
            return {
                title: programName,
                status: t('traineeReports:expired'),
            };
        }
        if (now < startDate) {
            return {
                title: programName,
                status: t('traineeReports:notStarted'),
            };
        }

        return {
            title: programName,
            status: t('traineeReports:inProgress'),
        };
    }

    const latestCompletedAt = results
        .map(r => r.completedAt)
        .filter((d): d is string => Boolean(d) && !Number.isNaN(new Date(d).getTime()))
        .reduce<string | null>((latest, curr) => {
            if (!latest) {
                return curr;
            }
            return new Date(curr) > new Date(latest) ? curr : latest;
        }, null);

    return {
        title: programName,
        status: latestCompletedAt
            ? t('traineeReports:completedOn', { date: formatDate(latestCompletedAt) })
            : t('traineeReports:na'),
    };
};

export const mapProfile = (
    t: TFunction,
    user: TenantUserReportDataFragment | null,
    formatAge: (date: string | Date | Dayjs) => string
): Profile => {
    return {
        name: user ? `${user.firstName || ''} ${user.lastName || ''}` : '',
        age: user && user.dateOfBirth ? formatAge(user.dateOfBirth) : t('traineeReports:na'),
        gender: user && user.gender ? parseGender(t, user.gender) : t('traineeReports:na'),
        heightCm: user && user.height ? user.height.toFixed(2) : t('traineeReports:na'),
    } as Profile;
};

export const mapTraineeReportData = (
    t: TFunction,
    results: AssessmentResultItemDataFragment[],
    teamResilienceItems: AssessmentResultItemDataFragment[],
    incompleteItems: AssessmentResultItemDataFragment[]
): TraineeReportModel => {
    // Trainer Notes section
    const { result: trainerNotes } = results.find(item => item.result.__typename === 'TrainersNotesResult') || {};
    const trainerNotesResult = trainerNotes as TrainersNotesAssessmentResultDataFragment | undefined;

    // Psychological Fitness section
    const psychologicalResults: PsychologicalResult[] = parsePsychologicalResults(
        t,
        results,
        teamResilienceItems,
        incompleteItems
    );

    // Physiological Fitness section
    const physiologicalResults: PhysiologicalResult[] = parsePhysiologicalResults(t, results, incompleteItems);

    // Cognitive Fitness section
    const cognitiveFitness: CognitiveFitness[] = parseCognitiveResults(t, results, incompleteItems);

    const report: TraineeReportModel = {
        trainerNotes: trainerNotesResult?.trainersNotes || t('traineeReports:na'),
        physiologicalFitness: physiologicalResults,
        psychologicalFitness: psychologicalResults,
        cognitiveFitness,
    };

    return report;
};

export const mapPerformanceResults = (
    t: TFunction,
    formatDate: (date: string | Date | Dayjs) => string,
    results: PerformanceResultListItemDataFragment[]
): Performance[] => {
    if (results.length === 0) {
        return [];
    }
    const defaultPerformanceResult = new Map<string, Performance>([
        [
            'IpptResult',
            {
                name: t('performance.ippt.title'),
                date: t('na'),
                result: [
                    {
                        label: t('performance.ippt.points'),
                        value: t('na'),
                    },
                    {
                        label: t('performance.ippt.pushUps'),
                        value: t('na'),
                    },
                    {
                        label: t('performance.ippt.sitUps'),
                        value: t('na'),
                    },
                    {
                        label: t('performance.ippt.pullUps'),
                        value: t('na'),
                    },
                    {
                        label: t('performance.ippt.runTime'),
                        value: t('na'),
                    },
                ],
            },
        ],
        [
            'SocResult',
            {
                name: t('performance.soc.title'),
                date: t('na'),
                result: [
                    {
                        label: t('performance.soc.status'),
                        value: t('na'),
                    },
                    {
                        label: t('performance.soc.completion'),
                        value: t('na'),
                    },
                ],
            },
        ],
        [
            'March20kmResult',
            {
                name: t('performance.march20km.title'),
                date: t('na'),
                result: [
                    {
                        label: t('performance.march20km.status'),
                        value: t('na'),
                    },
                    {
                        label: t('performance.march20km.completion'),
                        value: t('na'),
                    },
                ],
            },
        ],
        [
            'FinSwimResult',
            {
                name: t('performance.finSwim.title'),
                date: t('na'),
                result: [
                    {
                        label: t('performance.finSwim.timing'),
                        value: t('na'),
                    },
                ],
            },
        ],
        [
            'BmpResult',
            {
                name: t('performance.bmp.title'),
                date: t('na'),
                result: [
                    {
                        label: t('performance.bmp.percentage'),
                        value: t('na'),
                    },
                ],
            },
        ],
    ]);

    for (const performanceResult of results) {
        const { result, completedAt } = performanceResult;

        switch (result.__typename) {
            case 'IpptResult':
                defaultPerformanceResult.set('IpptResult', {
                    name: t('performance.ippt.title'),
                    date: completedAt ? formatDate(completedAt) : t('na'),
                    result: [
                        {
                            label: t('overallTraineeReport:performance.ippt.points'),
                            value: result.points ?? t('na'),
                        },
                        {
                            label: t('performance.ippt.pushUps'),
                            value: result.pushUps ?? t('na'),
                        },
                        {
                            label: t('performance.ippt.sitUps'),
                            value: result.sitUps ?? t('na'),
                        },
                        {
                            label: t('performance.ippt.pullUps'),
                            value: result.pullUps ?? t('na'),
                        },
                        {
                            label: t('performance.ippt.runTime'),
                            value: result.runTimeSec ? (formatSecondsAsMinSec(result.runTimeSec) ?? t('na')) : t('na'),
                        },
                    ],
                });
                break;
            case 'BmpResult':
                defaultPerformanceResult.set('BmpResult', {
                    name: t('performance.bmp.title'),
                    date: completedAt ? formatDate(completedAt) : t('na'),
                    result: [
                        {
                            label: t('performance.bmp.percentage'),
                            value: result.percentage ?? t('na'),
                        },
                    ],
                });
                break;
            case 'FinSwimResult':
                defaultPerformanceResult.set('FinSwimResult', {
                    name: t('performance.finSwim.title'),
                    date: completedAt ? formatDate(completedAt) : t('na'),
                    result: [
                        {
                            label: t('performance.finSwim.timing'),
                            value: result.timingSec ? (formatSecondsAsMinSec(result.timingSec) ?? t('na')) : t('na'),
                        },
                    ],
                });
                break;
            case 'March20kmResult':
                defaultPerformanceResult.set('March20kmResult', {
                    name: t('performance.march20km.title'),
                    date: completedAt ? formatDate(completedAt) : t('na'),
                    result: [
                        {
                            label: t('performance.march20km.status'),
                            value: result.result ?? t('na'),
                        },
                        {
                            label: t('performance.march20km.completion'),
                            value: result.timingSec ? (formatSecondsAsHoursMin(result.timingSec) ?? t('na')) : t('na'),
                        },
                    ],
                });
                break;
            case 'SocResult':
                defaultPerformanceResult.set('SocResult', {
                    name: t('performance.soc.title'),
                    date: completedAt ? formatDate(completedAt) : t('na'),
                    result: [
                        {
                            label: t('performance.soc.status'),
                            value: result.result ?? t('na'),
                        },
                        {
                            label: t('performance.soc.completion'),
                            value: result.timingSec ? (formatSecondsAsMinSec(result.timingSec) ?? t('na')) : t('na'),
                        },
                    ],
                });
                break;
            default:
                break;
        }
    }

    return Array.from(defaultPerformanceResult.values());
};
